/**
 * Live Focus subtypes from the create-session picker.
 * Chat Sessions columns and Create New Session must read from here.
 * Do not add mock stand-in kinds (Conflict, Theme, Logline).
 *
 * The plot session is the book's argument — the spine of what the
 * book proves. That door is book-level only. Series Focus is Series
 * Theme, Character, World Building, and Custom (#798).
 */

/** Writer-facing name of the plot session (book). Not a pile card. */
export const PLOT_DOOR_LABEL = "The book's argument";

export const PLOT_DOOR_DESCRIPTION_BOOK = 'The spine of what the book proves';

/** Writer-facing Series Theme door. Distinct from Book Theme. Not a gate. */
export const SERIES_THEME_LABEL = 'Series Theme';

export const SERIES_THEME_DESCRIPTION =
  'Optional. What this series is about underneath — not a book’s theme, and not required for Get Book Ideas';

/** Writer-facing World Building door at series. Rules plus setting. */
export const WORLD_BUILDING_LABEL = 'World Building';

export const WORLD_BUILDING_DESCRIPTION =
  'Rules plus setting, not place only — what magic costs, who can inherit, and the ground they stand on';

export const FOCUS_TEMPLATES = [
  { key: 'character', label: 'Character', emoji: '\u{1F464}', description: 'Focus on a character in depth' },
  { key: 'plot', label: PLOT_DOOR_LABEL, emoji: '\u{1F4C8}', description: PLOT_DOOR_DESCRIPTION_BOOK },
  { key: 'world', label: 'World', emoji: '\u{1F30D}', description: 'Build world elements' },
  { key: 'scene', label: 'Scene', emoji: '\u{1F3AC}', description: 'Focus on specific scenes' },
  { key: 'dialogue', label: 'Dialogue', emoji: '\u{1F4AC}', description: 'Work on character voice' },
];

/**
 * Series altitude (#798): Series Theme, Character, World Building, Custom.
 * No Plot. No Scene. No Dialogue. No “The book's argument.”
 */
export const SERIES_FOCUS_TEMPLATES = [
  { key: 'series_theme', label: SERIES_THEME_LABEL, emoji: '\u{1F4A1}', description: SERIES_THEME_DESCRIPTION },
  { key: 'character', label: 'Character', emoji: '\u{1F464}', description: 'A named person — who they are, what they want, how they change' },
  { key: 'world', label: WORLD_BUILDING_LABEL, emoji: '\u{1F30D}', description: WORLD_BUILDING_DESCRIPTION },
  { key: 'custom', label: 'Custom', emoji: '\u{270F}', description: 'A focus you name' },
];

export const FOCUS_TEMPLATE_KEYS = FOCUS_TEMPLATES.map((t) => t.key);

export const FOCUS_TEMPLATE_KEY_SET = new Set(FOCUS_TEMPLATE_KEYS);

export const SERIES_FOCUS_TEMPLATE_KEYS = SERIES_FOCUS_TEMPLATES.map((t) => t.key);

export const SERIES_FOCUS_TEMPLATE_KEY_SET = new Set(SERIES_FOCUS_TEMPLATE_KEYS);

export const SERIES_DISCOVERY_LABEL = 'Series Discovery';

export function focusTemplatesForAltitude(seriesAltitude) {
  return seriesAltitude ? SERIES_FOCUS_TEMPLATES : FOCUS_TEMPLATES;
}

export function focusColumnLabel(templateKey, seriesAltitude = false) {
  const primary = seriesAltitude ? SERIES_FOCUS_TEMPLATES : FOCUS_TEMPLATES;
  const fallback = seriesAltitude ? FOCUS_TEMPLATES : SERIES_FOCUS_TEMPLATES;
  const found = primary.find((t) => t.key === templateKey)
    || fallback.find((t) => t.key === templateKey);
  if (found) return found.label;
  if (templateKey === 'custom') return 'Custom';
  return String(templateKey || 'Focus')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
